import { put, call, takeLatest, select } from 'redux-saga/effects';
import intl from 'react-intl-universal';
import {
  login,
  ssologin,
  forgotPassword as forgotPasswordService,
  pinCodeRequest,
  resendCode,
  resetPassword,
  userLogout,
} from 'src/services/login.service';
import { push } from 'connected-react-router';

import { DASHBOARD_URL, CONSENT_URL, noData, validRoleCodes, IS_APP_IMMICHAT } from 'src/constants/constants.js';
import * as loginActions from './loginActions';
import * as loginTypes from './loginTypes';
import { LOGINPAGE, TFAPAGE, RPASSPAGE } from './constants';
import { clearPresistfromLocalStorge, enablePresist } from 'src/helpers';

const loginError = errorObject => {
  return errorObject.response ? errorObject.response : errorObject;
};

//const applicantKey = validRoleCodes.filter(item => item.roleType === 'applicant')[0].roleKey;
let applicantKey;
if (!IS_APP_IMMICHAT) {
  // taxchat
  applicantKey = validRoleCodes.filter(item => item.roleType === 'applicant')[0].roleKey;
} else {
  //immichat
  applicantKey = validRoleCodes.filter(item => item.roleType === 'immiChatApplicant')[0].roleKey;
}

export const isApplicant = permissions => permissions.filter(perm => perm.roleKey === applicantKey).length > 0;

export function* loginRequest({ stok, username, password }) {
  try {
    let userInfo;
    if (stok) {
      userInfo = yield call(ssologin, stok);
    } else {
      if (!username || !password) throw loginError(noData);
      userInfo = yield call(login, username, password);
    }
    if (userInfo.status !== 200) throw loginError(userInfo);
    if (userInfo.data.token) {
      if (isApplicant(userInfo.data.permissions)) {
        throw loginError({
          data: {
            error: {
              message: intl.get('login.isApplicant'),
            },
          },
        });
      }
      yield call(validateToken, userInfo.data);
    }
    const { changePassword, email, success, profileKey, token, isConsentAcceptanceNeeded, consentList } = userInfo.data;
    if (!success || success === 'false') throw loginError(userInfo);
    sessionStorage.setItem('consentList', JSON.stringify(consentList));
    const loginPage = (!token && profileKey) || changePassword === true ? TFAPAGE : LOGINPAGE;
    yield put(
      loginActions.loginSuccess({
        email: username ? username : email,
        userSuccess: success,
        profileKey: profileKey,
        token,
        loginPage,
        isConsentAcceptanceNeeded,
        consentList,
      }),
    );
    yield put(loginActions.clearLoginAttempts());
    if (!changePassword || changePassword !== true) {
      if (isConsentAcceptanceNeeded) {
        sessionStorage.setItem('CN', 'CN');
        return yield put(push(CONSENT_URL));
      }
      if (token) {
        enablePresist();
        return yield put(push(DASHBOARD_URL));
      }
    }
  } catch (error) {
    console.error(error);
    const data = error && error.data ? error.data : { error };
    yield put(loginActions.loginFailure(data));
    clearPresistfromLocalStorge();
  }
}

export function* validateToken(data) {
  const newUserInfo = { ...data, personId: data.profileKey };
  yield put(loginActions.setUserInfoFromStorage({ token: data.token, userInfo: newUserInfo }));
  const token = data.token;
  sessionStorage.setItem('token', token);
  sessionStorage.setItem('isSessionActive', true);
  sessionStorage.setItem('userInfo', JSON.stringify(newUserInfo));
  yield put(loginActions.userValidateSuccess({ userInfo: newUserInfo }));
  yield put(loginActions.updateUserRoles({ userInfo: newUserInfo }));
  return true;
}

export function* userLogoutToken() {
  const token = yield select(state => state.login.token);
  if (!token || token === null) {
    return true;
  } else {
    const resp = yield call(userLogout, token);
    if (resp.data.success && resp.data.success === true) {
      yield put(loginActions.clearSession());
      return true;
    } else {
      return false;
    }
  }
}

export function* validatePINCodeRequest({ code, profileKey }) {
  try {
    const { flow } = yield select(state => state.login);
    const response = yield call(pinCodeRequest, code, profileKey, flow);
    if (response.status !== 200) throw new loginError(response);
    const { success, ...restData } = response.data;
    if (!success || success === 'false') throw loginError(response);
    const loginPage = success === true ? RPASSPAGE : LOGINPAGE;
    yield put(loginActions.validateCodeSuccess({ pinSuccess: success, profileKey, loginPage, ...restData }));
    yield put(loginActions.clearErrors());
  } catch (error) {
    const data = error && error.data ? error.data : { error };
    yield put(loginActions.validateCodeFailure(data));
  }
}

export function* forgotPasswordRequest({ email }) {
  try {
    const response = yield call(forgotPasswordService, email);
    if (!response.success || response.success === 'false') throw new loginError(response);
    const { success, profileKey } = response;
    if (!success || success === 'false') throw new loginError(response);
    const loginPage = profileKey ? TFAPAGE : LOGINPAGE;
    yield put(loginActions.forgotPasswordSuccess({ email: email, userSuccess: success, profileKey: profileKey, loginPage }));
    yield put(loginActions.clearErrors());
  } catch (error) {
    const data = error && error.data ? error.data : { error };
    yield put(loginActions.forgotPasswordFailure(data));
  }
}

export function* resendCodeRequest({ email }) {
  try {
    const { profileKey } = yield select(state => state.login);
    const response = yield call(resendCode, email, profileKey);
    if (!response.success || response.success === 'false') throw new loginError(response);
    yield put(loginActions.resendCodeSuccess(response.message));
  } catch (error) {
    const data = error && error.data ? error.data : { error };
    yield put(loginActions.resendCodeFailure(data));
  }
}
export function* clearAll() {
  yield put(loginActions.clearErrors());
}

export function* resetPasswordSaga({ password }) {
  try {
    const { authCode, profileKey } = yield select(state => state.login);

    const response = yield call(resetPassword, authCode, password, profileKey);
    if (!response.success || response.success === 'false') throw new loginError(response);
    yield put(loginActions.resetPasswordSuccess({ loginPage: LOGINPAGE }));
    yield put(loginActions.clearErrors());
  } catch (error) {
    const data = error && error.data ? error.data : { error };
    yield put(loginActions.resendCodeFailure(data));
  }
}

const myLoginSaga = [
  takeLatest(loginTypes.POST_LOGIN_PENDING, loginRequest),
  takeLatest(loginTypes.USER_VALIDATE_TOKEN, validateToken),
  takeLatest(loginTypes.VALIDATE_CODE_PENDING, validatePINCodeRequest),
  takeLatest(loginTypes.RETURN_TO_LOGIN, clearAll),
  takeLatest(loginTypes.GOTO_FORGOT_PASSWORD, clearAll),
  takeLatest(loginTypes.GOTO_LOGIN, clearAll),
  takeLatest(loginTypes.POST_FORGOT_PASSWORD_PENDING, forgotPasswordRequest),
  takeLatest(loginTypes.RESEND_CODE_PENDING, resendCodeRequest),
  takeLatest(loginTypes.RESET_PASSWORD_PENDING, resetPasswordSaga),
  takeLatest(loginTypes.LOGOUT_SESSION, userLogoutToken),
];

export default myLoginSaga;
